"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { useScrollShadow } from "@/hooks/use-scroll-shadow";
import { cx } from "@/lib/tailwind-utils";
interface ScrollShadowState {
  hasScrolledDown: boolean;
  hasMoreContentBelow: boolean;
}
interface ScrollShadowMeta {
  scrollRef: (node: HTMLElement | null) => void;
}
interface ScrollShadowContextValue {
  state: ScrollShadowState;
  meta: ScrollShadowMeta;
}
const ScrollShadowContext = React.createContext<ScrollShadowContextValue | null>(null);
function useScrollShadowContext(): ScrollShadowContextValue {
  const context = React.use(ScrollShadowContext);
  if (!context) {
    throw new Error("ScrollShadow parts must be rendered within a Root");
  }
  return context;
}
function ScrollShadowRoot(t0) {
  const $ = _c(19);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    scrollRef,
    hasScrolledDown,
    hasMoreContentBelow
  } = useScrollShadow();
  let t1;
  if ($[3] !== hasMoreContentBelow || $[4] !== hasScrolledDown) {
    t1 = {
      hasScrolledDown,
      hasMoreContentBelow
    };
    $[3] = hasMoreContentBelow;
    $[4] = hasScrolledDown;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== scrollRef) {
    t2 = {
      scrollRef
    };
    $[6] = scrollRef;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2) {
    t3 = {
      state: t1,
      meta: t2
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const value = t3;
  let t4;
  if ($[11] !== className) {
    t4 = cx("relative flex min-h-0 flex-1 overflow-hidden rounded-md", className);
    $[11] = className;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== props || $[14] !== t4) {
    t5 = <div className={t4} {...props} />;
    $[13] = props;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t5 || $[17] !== value) {
    t6 = <ScrollShadowContext value={value}>{t5}</ScrollShadowContext>;
    $[16] = t5;
    $[17] = value;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}
function ScrollShadowViewport(t0) {
  const $ = _c(13);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  const {
    meta: t1
  } = useScrollShadowContext();
  const {
    scrollRef
  } = t1;
  let t2;
  if ($[4] !== ref || $[5] !== scrollRef) {
    t2 = node => {
      scrollRef(node);
      if (typeof ref === "function") {
        ref(node);
      } else {
        if (ref) {
          ref.current = node;
        }
      }
    };
    $[4] = ref;
    $[5] = scrollRef;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== className) {
    t3 = cx("h-auto min-h-0 w-full overflow-y-auto [&]:[-webkit-overflow-scrolling:touch]", className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props || $[10] !== t2 || $[11] !== t3) {
    t4 = <div ref={t2} className={t3} {...props} />;
    $[9] = props;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function ScrollShadowTop(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    state: t1
  } = useScrollShadowContext();
  const {
    hasScrolledDown
  } = t1;
  if (!hasScrolledDown) {
    return null;
  }
  let t2;
  if ($[3] !== className) {
    t2 = cx("pointer-events-none absolute inset-x-0 top-0 h-[5%] opacity-80", "bg-gradient-to-b from-background via-background/80 to-transparent", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== props || $[6] !== t2) {
    t3 = <div aria-hidden={true} className={t2} {...props} />;
    $[5] = props;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function ScrollShadowBottom(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    state: t1
  } = useScrollShadowContext();
  const {
    hasMoreContentBelow
  } = t1;
  if (!hasMoreContentBelow) {
    return null;
  }
  let t2;
  if ($[3] !== className) {
    t2 = cx("pointer-events-none absolute inset-x-0 bottom-0 h-[5%] opacity-80", "bg-gradient-to-t from-background via-background/80 to-transparent", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== props || $[6] !== t2) {
    t3 = <div aria-hidden={true} className={t2} {...props} />;
    $[5] = props;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
ScrollShadowRoot.displayName = "ScrollShadow.Root";
ScrollShadowViewport.displayName = "ScrollShadow.Viewport";
ScrollShadowTop.displayName = "ScrollShadow.Top";
ScrollShadowBottom.displayName = "ScrollShadow.Bottom";

/**
 * Scrollable area with optional top/bottom shadow affordances.
 *
 * Compose the parts you need — each one owns its own styles:
 *
 * ```tsx
 * <ScrollShadow.Root>
 *   <ScrollShadow.Viewport className="p-4">{children}</ScrollShadow.Viewport>
 *   <ScrollShadow.Top />
 *   <ScrollShadow.Bottom />
 * </ScrollShadow.Root>
 * ```
 */
export const ScrollShadow = {
  Root: ScrollShadowRoot,
  Viewport: ScrollShadowViewport,
  Top: ScrollShadowTop,
  Bottom: ScrollShadowBottom
};